import React, { useState, useRef, useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { useContactInfo, getPrimaryContact, getContactByType } from "../hooks/useContactInfo";
import { useNavigate, useLocation } from "react-router-dom";

// Helper to shorten day labels
function shortenLabel(label: string) {
  return label
    .replace(/Monday\s*-\s*Thursday/i, 'Mon – Thu')
    .replace(/Friday\s*-\s*Saturday/i, 'Fri – Sat')
    .replace(/Sunday/i, 'Sun');
}

// Helper to generate Google Maps embed URL from address
function getGoogleMapsEmbedUrl(address: string) {
  const encoded = encodeURIComponent(address);
  return `https://www.google.com/maps?q=${encoded}&output=embed`;
}

const Contact: React.FC = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });
  const navigate = useNavigate();
  const location = useLocation();
  const formRef = useRef<HTMLDivElement>(null);

  // Scroll to form if subject param is present
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('subject') && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [location.search]);

  // Fetch contact info from DB
  const { data: contactInfo } = useContactInfo();
  const address = getPrimaryContact(contactInfo, 'ADDRESS');
  const phone = getPrimaryContact(contactInfo, 'PHONE');
  const email = getPrimaryContact(contactInfo, 'EMAIL');
  const hours = getContactByType(contactInfo, 'HOURS');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch('/.netlify/functions/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
        }),
      });
      const data = await res.json();
      if (data.success) {
        toast({
          title: 'Message Sent!',
          description: "We'll get back to you as soon as possible.",
        });
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: data.error || 'Failed to send message. Please try again later.',
        });
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to send message. Please try again later.',
      });
    }
  };

  return (
    <div className="min-h-screen bg-lounge-dark text-lounge-light">
      <Navbar />

      {/* Page Header */}
      <div className="relative pt-20 pb-16 md:pt-24 md:pb-20 bg-lounge-charcoal">
        <div className="container-custom">
          <h1 className="section-title text-center">Contact Us</h1>
          <p className="section-subtitle text-center max-w-3xl mx-auto">
            Get in touch with Seven8Nine Hookah Lounge for inquiries, bookings, or feedback.
          </p>
        </div>
      </div>

      {/* Contact Information */}
      <section className="section-padding bg-lounge-dark">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {/* Phone */}
            <div className="bg-lounge-charcoal p-6 rounded-lg flex flex-col items-center text-center border border-lounge-gold/20">
              <div className="bg-lounge-gold/10 p-4 rounded-full mb-4">
                <Phone size={24} className="text-lounge-gold" />
              </div>
              <h3 className="text-xl font-display font-semibold mb-2 text-lounge-gold">Phone</h3>
              {phone?.value ? (
                <a href={`tel:${phone.value}`} className="text-lounge-light/80 hover:text-lounge-gold transition-colors">{phone.value}</a>
              ) : (
                <span className="text-lounge-light/80">(204) 555-1234</span>
              )}
            </div>

            {/* Email */}
            <div className="bg-lounge-charcoal p-6 rounded-lg flex flex-col items-center text-center border border-lounge-gold/20">
              <div className="bg-lounge-gold/10 p-4 rounded-full mb-4">
                <Mail size={24} className="text-lounge-gold" />
              </div>
              <h3 className="text-xl font-display font-semibold mb-2 text-lounge-gold">Email</h3>
              {email?.value ? (
                <a href={`mailto:${email.value}`} className="text-lounge-light/80 hover:text-lounge-gold transition-colors">{email.value}</a>
              ) : (
                <span className="text-lounge-light/80">info@seven8nine.com</span>
              )}
            </div>

            {/* Address */}
            <div className="bg-lounge-charcoal p-6 rounded-lg flex flex-col items-center text-center border border-lounge-gold/20">
              <div className="bg-lounge-gold/10 p-4 rounded-full mb-4">
                <MapPin size={24} className="text-lounge-gold" />
              </div>
              <h3 className="text-xl font-display font-semibold mb-2 text-lounge-gold">Location</h3>
              <p className="text-lounge-light/80">
                {address?.value || "123 Main Street, Winnipeg, MB R3C 1A2"}
              </p>
            </div>

            {/* Hours */}
            <div className="bg-lounge-charcoal p-6 rounded-lg flex flex-col items-center text-center border border-lounge-gold/20">
              <div className="bg-lounge-gold/10 p-4 rounded-full mb-4">
                <Clock size={24} className="text-lounge-gold" />
              </div>
              <h3 className="text-xl font-display font-semibold mb-2 text-lounge-gold">Opening Hours</h3>
              <ul className="text-lounge-light/80 space-y-1 w-full max-w-xs mx-auto text-left">
                {hours.length > 0 ? (
                  hours.map((h) => (
                    <li key={h.id} className="flex justify-between items-center">
                      <span className="min-w-max pr-2">{shortenLabel(h.label)}</span>
                      <span className="text-lounge-light/80">{h.value}</span>
                    </li>
                  ))
                ) : (
                  <>
                    <li className="flex justify-between items-center">
                      <span className="min-w-max pr-2">Mon – Thu</span>
                      <span className="text-lounge-light/80">4:00 PM – 12:00 AM</span>
                    </li>
                    <li className="flex justify-between items-center">
                      <span className="min-w-max pr-2">Fri – Sat</span>
                      <span className="text-lounge-light/80">4:00 PM – 2:00 AM</span>
                    </li>
                    <li className="flex justify-between items-center">
                      <span className="min-w-max pr-2">Sun</span>
                      <span className="text-lounge-light/80">4:00 PM – 12:00 AM</span>
                    </li>
                  </>
                )}
              </ul>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Map */}
            <div className="h-[400px] rounded-lg overflow-hidden">
              <iframe 
                src={address?.value ? getGoogleMapsEmbedUrl(address.value) : "https://www.google.com/maps?q=123%20Main%20St,%20Winnipeg,%20MB&output=embed"}
                width="100%" 
                height="100%" 
                style={{ border: 0 }} 
                allowFullScreen={true} 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="Seven8Nine Hookah Lounge Location"
              ></iframe>
            </div>

            {/* Contact Form */}
            <div ref={formRef}>
              <h2 className="text-2xl md:text-3xl font-display font-bold mb-6 text-lounge-gold">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-lounge-light mb-2">Your Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full bg-lounge-charcoal border border-lounge-gold/30 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-lounge-gold/50"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-lounge-light mb-2">Your Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-lounge-charcoal border border-lounge-gold/30 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-lounge-gold/50"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-lounge-light mb-2">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full bg-lounge-charcoal border border-lounge-gold/30 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-lounge-gold/50"
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-lounge-light mb-2">Subject</label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full bg-lounge-charcoal border border-lounge-gold/30 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-lounge-gold/50"
                    >
                      <option value="">Select a subject</option>
                      <option value="general">General Inquiry</option>
                      <option value="reservation">Table Reservation</option>
                      <option value="event">Private Event</option>
                      <option value="book-event">Book Event</option>
                      <option value="feedback">Feedback</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-lounge-light mb-2">Your Message</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className="w-full bg-lounge-charcoal border border-lounge-gold/30 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-lounge-gold/50"
                  ></textarea>
                </div>

                <button type="submit" className="btn-primary">Send Message</button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Information */}
      <section className="section-padding bg-lounge-charcoal">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-display font-bold mb-6 text-lounge-gold text-center">Reservations</h2>
            <p className="text-center text-lounge-light/80 mb-8">
              For the best experience, we recommend making a reservation, especially for weekends and special events.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-lounge-dark p-6 rounded-lg border border-lounge-gold/20">
                <h3 className="text-xl font-display font-semibold mb-3 text-lounge-gold">Regular Reservations</h3>
                <p className="text-lounge-light/80 mb-4">
                  For groups of any size, we recommend calling us at {phone?.value ? (
                    <a href={`tel:${phone.value}`} className="text-lounge-gold hover:underline">{phone.value}</a>
                  ) : (
                    <span className="text-lounge-gold">(204) 555-1234</span>
                  )} or using our online reservation system to book your table in advance.
                </p>
                <button className="btn-outline w-full" onClick={() => navigate('/contact?subject=reservation')}>Book a Table</button>
              </div>
              
              <div className="bg-lounge-dark p-6 rounded-lg border border-lounge-gold/20">
                <h3 className="text-xl font-display font-semibold mb-3 text-lounge-gold">Private Events</h3>
                <p className="text-lounge-light/80 mb-4">
                  Planning a special celebration? We offer private event services with customized packages. Contact us for more information.
                </p>
                <button className="btn-outline w-full" onClick={() => navigate('/contact?subject=event')}>Inquire About Events</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
